import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, Hand } from "lucide-react";
import { api } from "@/lib/api";
import { Pill, Loader, Empty } from "@/components/Bits";
import { useAuth } from "@/context/AuthContext";

const CATS = ["all", "awareness", "explainer", "opinion", "research", "ground-report"];
const FALLBACK =
  "https://images.unsplash.com/photo-1517457373958-b7bdd4587205?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxjb21tdW5pdHklMjBnYXRoZXJpbmclMjBwZW9wbGUlMjBjb25uZWN0aW5nfGVufDB8fHx8MTc4NjIwNDkxNHww&ixlib=rb-4.1.0&q=85";

const Blogs = () => {
  const { user, login } = useAuth();
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api
      .get("/blogs", { params: { category: cat, q: q || undefined } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [cat, q]);

  const [lead, ...rest] = items;

  return (
    <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16" data-testid="blogs-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">Awareness</span>
          <h1 className="mt-3 font-bebas text-5xl sm:text-6xl tracking-wide uppercase">Blogs</h1>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border border-white/10 px-3 h-11 bg-white/[0.03] focus-within:border-neon/60 transition-colors">
            <Search size={15} className="text-white/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search writing" data-testid="blogs-search" className="bg-transparent outline-none text-sm w-52 placeholder:text-white/30" />
          </div>
          {user ? (
            <Link to="/blogs/new" data-testid="submit-blog-btn" className="bg-neon px-6 h-11 grid place-items-center text-[12px] font-bold uppercase tracking-[0.18em] hover:pc-glow transition-shadow duration-200">
              Submit a piece
            </Link>
          ) : (
            <button onClick={login} data-testid="blogs-login-btn" className="border border-white/20 px-6 h-11 text-[12px] font-bold uppercase tracking-[0.18em] hover:border-neon hover:text-neon transition-colors">
              Sign in to write
            </button>
          )}
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-2">
        {CATS.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`blogs-filter-${c}`}
            className={`px-4 h-9 text-[11px] font-bold uppercase tracking-[0.18em] border transition-colors duration-200 ${
              cat === c ? "border-neon text-neon bg-neon/10" : "border-white/12 text-white/50 hover:text-white"
            }`}>
            {c}
          </button>
        ))}
      </div>

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <div className="mt-12"><Empty text="No published pieces here yet. Submit one — editors review every entry." testid="blogs-empty" /></div>
      ) : (
        <>
          <Link to={`/blogs/${lead.blog_id}`} data-testid="blog-lead-card" className="mt-12 group grid gap-0 md:grid-cols-12 border border-white/10 bg-ink-surface hover:border-neon/50 transition-colors duration-300">
            <div className="md:col-span-6 relative h-64 md:h-full overflow-hidden">
              <img src={lead.cover_image || FALLBACK} alt={lead.title} className="h-full w-full object-cover opacity-70 group-hover:opacity-95 group-hover:scale-105 transition-[opacity,transform] duration-500" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink-surface/70" />
            </div>
            <div className="md:col-span-6 p-8 lg:p-12">
              <div className="flex gap-2"><Pill tone="neon">Featured</Pill><Pill>{lead.category}</Pill></div>
              <h2 className="mt-6 font-bebas text-4xl lg:text-5xl tracking-wide uppercase leading-[0.95] group-hover:text-neon transition-colors">{lead.title}</h2>
              <p className="mt-4 text-sm text-white/55 leading-relaxed">{lead.summary}</p>
              <div className="mt-8 flex items-center gap-5 text-[11px] uppercase tracking-[0.18em] text-white/40">
                <span>{lead.author_name}</span>
                <span className="flex items-center gap-1.5"><Clock size={12} /> {lead.read_minutes} min</span>
                <span className="flex items-center gap-1.5"><Hand size={12} /> {lead.claps}</span>
              </div>
            </div>
          </Link>

          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {rest.map((b, i) => (
              <Link key={b.blog_id} to={`/blogs/${b.blog_id}`} data-testid={`blog-card-${i}`}
                className="group border border-white/10 bg-ink-surface hover:border-neon/50 hover:-translate-y-1 transition-[transform,border-color] duration-300">
                <div className="relative h-40 overflow-hidden">
                  <img src={b.cover_image || FALLBACK} alt={b.title} className="h-full w-full object-cover opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink-surface to-transparent" />
                </div>
                <div className="p-6">
                  <Pill>{b.category}</Pill>
                  <h3 className="mt-4 font-outfit text-lg font-bold leading-snug group-hover:text-neon transition-colors">{b.title}</h3>
                  <p className="mt-2 text-sm text-white/50 line-clamp-2">{b.summary}</p>
                  <div className="mt-5 flex items-center gap-4 text-[11px] uppercase tracking-[0.18em] text-white/35">
                    <span>{b.author_name}</span>
                    <span className="flex items-center gap-1.5"><Clock size={12} /> {b.read_minutes} min</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Blogs;
